import { useCallback, useEffect, useMemo, useState } from "react";
import { type PropsWithChildren, createContext } from "react";

import {
  getCookieConsent,
  removeCookieConsent,
  setCookieConsent,
} from "@punto/frontend-shared/utils/cookie-consent";

export interface CookieConsentContextValue {
  /** true if user has made a choice (accept or reject) */
  hasConsent: boolean;
  /** true = accepted, false = rejected, null = pending */
  consentGiven: boolean | null;
  /** Accept analytics cookies */
  acceptCookies: () => void;
  /** Reject analytics cookies */
  rejectCookies: () => void;
  /** Reset consent to show banner again */
  resetConsent: () => void;
}

export const CookieConsentContext =
  createContext<CookieConsentContextValue | null>(null);

export const CookieConsentProvider = ({ children }: PropsWithChildren) => {
  const [consentGiven, setConsentGiven] = useState<boolean | null>(() => {
    const consent = getCookieConsent();
    return consent?.analytics ?? null;
  });

  // Re-check consent on mount (for SSR hydration)
  useEffect(() => {
    const consent = getCookieConsent();
    setConsentGiven(consent?.analytics ?? null);
  }, []);

  const acceptCookies = useCallback(() => {
    setCookieConsent(true);
    setConsentGiven(true);
  }, []);

  const rejectCookies = useCallback(() => {
    setCookieConsent(false);
    setConsentGiven(false);
  }, []);

  const resetConsent = useCallback(() => {
    removeCookieConsent();
    setConsentGiven(null);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      hasConsent: consentGiven !== null,
      consentGiven,
      acceptCookies,
      rejectCookies,
      resetConsent,
    }),
    [consentGiven, acceptCookies, rejectCookies, resetConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
